import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Communication,
  CommunicationsQuery,
  CommunicationsResponse,
  ComposeContact,
  ComposeEmailPayload,
  ComposePreview,
  EmailTemplatesResponse,
} from './communication.model';

/**
 * TVG-343: HTTP wrapper for the Communications page and for the compose box
 * wherever it is opened from.
 *
 * Gated server-side on the `communications` toggle (404 when off), so a page
 * that somehow renders with the toggle off still cannot read or send anything.
 */
@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  list(query: CommunicationsQuery = {}): Observable<CommunicationsResponse> {
    let params = new HttpParams();

    if (query.channel) {
      params = params.set('channel', query.channel);
    }

    if (query.kind) {
      params = params.set('kind', query.kind);
    }

    if (query.venueId != null) {
      params = params.set('venue_id', query.venueId);
    }

    if (query.search) {
      params = params.set('search', query.search);
    }

    if (query.pageNum != null) {
      params = params.set('pageNum', query.pageNum);
    }

    if (query.pageSize != null) {
      params = params.set('pageSize', query.pageSize);
    }

    return this.http.get<CommunicationsResponse>(`${this.apiUrl}/api/communications`, { params });
  }

  templates(): Observable<EmailTemplatesResponse> {
    return this.http.get<EmailTemplatesResponse>(`${this.apiUrl}/api/communications/templates`);
  }

  /** Contacts this staff member may write to, for the compose box's To picker. */
  contacts(search: string | null = null): Observable<{ data: ComposeContact[] }> {
    let params = new HttpParams();

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<{ data: ComposeContact[] }>(`${this.apiUrl}/api/communications/contacts`, { params });
  }

  /**
   * Fill the placeholders server-side so the preview is the email itself
   * rather than a second implementation of the same substitution.
   */
  preview(contactId: number, subject: string, body: string): Observable<ComposePreview> {
    return this.http.post<ComposePreview>(`${this.apiUrl}/api/communications/preview`, {
      contact_id: contactId,
      subject,
      body,
    });
  }

  send(payload: ComposeEmailPayload): Observable<{ data: Communication }> {
    return this.http.post<{ data: Communication }>(`${this.apiUrl}/api/communications`, payload);
  }

  /**
   * Queue a failed staff email again. The same row is reused, so the response
   * is the same line with its status back at Queued.
   */
  resend(id: number): Observable<{ data: Communication }> {
    return this.http.post<{ data: Communication }>(`${this.apiUrl}/api/communications/${id}/resend`, {});
  }
}
